import { Injectable } from '@angular/core';
import {
    CredentialsTokensType,
    Customer,
    CustomersType,
    DynamicField,
    DynamicSections,
    PaymentMethodsType,
    PaymentRequestException,
    PaymentRequestExceptionType,
    Source,
    SprintPayCustomer,
} from '@cargos/sprintpay-models';
import { CustomerAccount, CustomerProfile } from '@cargos/sprintpay-models/dist/src/customer/customer';
import {
    AuthService,
    CustomerApiService,
    LoggerService,
    LoggerUser,
    StatService,
    TaxAddressPayload,
    TokenService,
} from '@cargos/sprintpay-services';
import { SalesTaxConfirmationResponse, SalesTaxInfo, TaxEligibleResponse } from '@cargos/sprintpay-ui';
import { BehaviorSubject, Observable, map, of } from 'rxjs';
import { DynamicDialogService } from '../components';
import { User } from '../models/customers';
import {
    getCompanyCustomerReferenceMethod,
    getCustomerInformationMethod,
    getPaymentCustomerReferenceMethod,
    getPaymentDetailsMethod,
} from '../models/payment/payment';
import { userTypes } from '../utils/constants';
import { Companies } from '../utils/requester-flow-levels/companies';

@Injectable({
    providedIn: 'root',
})
export class CustomerService {
    private user = new BehaviorSubject<User>(User.fromJson({}));

    constructor(
        private authService: AuthService,
        private tokenService: TokenService,
        private dynamicDialogService: DynamicDialogService,
        private customerApiService: CustomerApiService,
        private readonly statService: StatService,
        private readonly loggerService: LoggerService
    ) {}

    addCustomerTermsOfUse(ip: string): Observable<CredentialsTokensType> {
        const customerID = this.customer_id;
        const token = this.tokenService.instant_refresh_token;

        return this.authService.addCustomerTermsOfUse(customerID, ip, token);
    }

    /**
     * Get SprintPayCustomer
     * @returns an object of current user
     */
    getCompleteCustomer(): Observable<SprintPayCustomer | null> {
        return this.tokenService.getCurrentToken$().pipe(
            map(() => {
                this.setLoggerUser();
                const user = this.tokenService.getDecodedToken()?.SPRINT_PAY_CUSTOMER;

                return user ? (JSON.parse(user) as SprintPayCustomer) : null;
            })
        );
    }

    /**
     * Registers the current user in the logger with company attribution so all logs
     * (including Datadog) can be segmented by customer and company.
     */
    setLoggerUser(): void {
        const customer = this.getCustomer();
        if (!customer) {
            return;
        }
        const customerId = this.customer_id;
        this.loggerService.registerUser({
            id: customerId ? String(customerId) : undefined,
            email: this.getCustomerEmail() ?? '',
            company_name: this.getCustomerCompanyName() || (this.getCompanyName() ?? ''),
        } as LoggerUser);
    }

    /**
     * Get current user
     * @returns an object of current user
     */
    getCurrentUser(): Observable<Customer | undefined> {
        return this.getCompleteCustomer().pipe(
            map((sprintPayCustomer: SprintPayCustomer | null) => {
                return sprintPayCustomer?.customer;
            })
        );
    }

    /**
     * Get customer profile
     * @returns {CustomerProfile} an object of customer profile
     */
    getCustomerProfile(): Observable<CustomerProfile | undefined> {
        return this.getCurrentUser().pipe(
            map((customer: Customer | undefined) => {
                return customer?.customerProfile;
            })
        );
    }

    /**
     * Get customer
     * @returns an object of Customer
     */
    getCustomer(): Customer {
        return this.current_user.customer;
    }

    getCustomerEmail(): string {
        return this.current_user?.customer?.email;
    }

    /**
     * Get the customer's top-level company name
     * @returns the company name, or an empty string if not present
     */
    getCustomerCompanyName(): string {
        return this.current_user?.customer?.companyName ?? '';
    }

    getUserName(): Observable<string | undefined> {
        return this.getCurrentUser().pipe(map((customer) => customer?.firstName));
    }

    get current_user(): SprintPayCustomer {
        const user = this.tokenService.getDecodedToken()?.SPRINT_PAY_CUSTOMER;

        return user ? JSON.parse(user) : ({} as SprintPayCustomer);
    }

    get customer_type(): CustomersType {
        return this.current_user.customerType;
    }

    getCustomerType(): Observable<CustomersType | undefined> {
        return this.getCompleteCustomer().pipe(map((customer) => customer?.customerType));
    }

    get authorization_token_id(): number {
        return this.tokenService.getDecodedToken().AUTHORIZATION_TOKEN_ID;
    }

    get source(): Source {
        const source = this.tokenService.getDecodedToken().SOURCE;

        return source ? JSON.parse(source) : {};
    }

    get supported_destinations(): string {
        return this.source.supportedDestinations || '';
    }

    get terms_of_use_effectiveDate(): string {
        return this.getCustomer().effectiveDate;
    }

    get is_terms_of_use_accepted(): boolean {
        return this.getCustomer().acceptTerms;
    }

    get customer_id(): number {
        return this.current_user.id;
    }

    getCompanyName(): string | null {
        if (this.isUserDomain()) {
            return this.getCompanyDomainName();
        } else if (this.getCustomer().expeditors) {
            return Companies.EXPEDITORS;
        }

        const customer = this.getCustomer();
        return customer?.approvalLevels?.company?.name?.toLowerCase() || null;
    }

    getCompanyId(): number | null {
        const customer = this.getCustomer();
        return customer?.approvalLevels?.company?.id || null;
    }

    isCompanyUser(): boolean {
        return !!this.getCompanyName() || this.isUserDomain();
    }

    isExpeditorsUser(): boolean {
        return this.getCustomer().expeditors;
    }

    getFirstLocationId(): number | null {
        const customer = this.getCustomer();
        return customer?.approvalLevels?.company?.firstLocation?.id || null;
    }

    isRequestor(): boolean {
        const customer = this.getCustomer();
        return !!customer?.approvalLevels?.company?.name && !!customer?.approvalLevels?.company?.startingLevel;
    }

    isRequestorUser(): boolean {
        const customer = this.getCustomer();
        return (
            !!customer?.userType?.toLowerCase().includes('requestor') &&
            !!customer?.approvalLevels?.company?.name &&
            !!customer?.approvalLevels?.company?.startingLevel
        );
    }

    getUserDomain(domain: string): boolean {
        const customer = this.getCustomer();
        return !!customer.email.includes(domain);
    }

    isUserDomain(): boolean {
        const email = this.getCustomerEmail();

        return !!Object.values(Companies.COMPANIES_WITH_CONFIG_BY_DOMAIN).find((value) => email.includes(value));
    }

    getCompanyDomainName(): string {
        const email = this.getCustomerEmail();

        return Object.keys(Companies.COMPANIES_WITH_CONFIG_BY_DOMAIN).find((value) => email.includes(value)) || '';
    }

    /**
     * This configuration is based on the company's user
     */
    setUser(user: User): void {
        this.user.next(user);
    }

    private getUser$(): Observable<User | null> {
        return this.user.asObservable();
    }

    get instant_customer_fields(): DynamicField[] {
        return this.user?.value?.dynamicFields || [];
    }

    /**
     * @returns This function returns all the static fields the user needs to make a payment
     */
    getCustomerFields(): Observable<DynamicField[]> {
        return this.getUser$().pipe(
            map((user) => {
                return (
                    user?.dynamicFields?.filter((field) => {
                        const isUserDomain = this.isUserDomain();

                        if (isUserDomain) {
                            return !!field.emailDomain;
                        }

                        return true;
                    }) || []
                );
            })
        );
    }

    getCustomerSections(): Observable<DynamicSections[]> {
        return this.getUser$().pipe(
            map((user) => {
                return user?.dynamicSections || [];
            })
        );
    }

    hasGlCodes(): boolean {
        return this.getCustomer().hasGlCodes;
    }

    getCustomerReferenceFieldsBuilder(): getPaymentCustomerReferenceMethod {
        return this.user.value.getPaymentCustomerReference;
    }

    getCustomerPaymentDetailsFieldsBuilder(): getPaymentDetailsMethod {
        return this.user.value.getPaymentDetails;
    }

    getCustomerInformationFieldsBuilder(): getCustomerInformationMethod {
        return this.user.value.getCustomerInformation;
    }

    getCompanyCustomerReferenceFieldsBuilder(): getCompanyCustomerReferenceMethod {
        return this.user.value.getCompanyCustomerReference;
    }

    getPaymentMethodForNewPayments(): PaymentMethodsType {
        const profile = this.getCustomerProfile();
        return profile['paymentMethodForNewPayments'] || '';
    }

    /**
     * @method isUniqueRequestor()
     * @description Checks if the current user is a unique requestor.
     */
    isUniqueRequestor(): boolean {
        return !!this.getCustomer()?.userType?.toLowerCase().includes(userTypes.UNIQUE_REQUESTOR);
    }

    getInvoiceCheckLimit(): number | null {
        const customer: Customer | null = this.getCustomer();
        return customer?.invoiceCheckLimit || null;
    }

    hasAutomatedDebits$(): Observable<{
        customerAutomatedDebit: boolean;
        customerAccountAutomatedDebit: boolean;
    }> {
        return of(this.getCustomer()).pipe(
            map((customer) => {
                return {
                    customerAutomatedDebit: !!customer?.isAutomatedDebitEnabled,
                    customerAccountAutomatedDebit: !!customer?.customerAccount?.isAutomatedDebitEnabled,
                };
            })
        );
    }

    isCTALendingEnabled(): boolean {
        return this.getCustomer().ctaLendingEnabled || false;
    }

    isPaymentsNotInvoicedEnabled(): boolean {
        return this.getCustomer().isPaymentsNotInvoicedEnabled || false;
    }

    getCustomerAccount$(): Observable<CustomerAccount | undefined> {
        return this.getCurrentUser().pipe(map((customer) => customer?.customerAccount));
    }

    getCustomerAccountId(): number | undefined {
        const customerAccount = this.getCustomer().customerAccount;
        return customerAccount ? customerAccount.id : undefined;
    }

    getCustomerAccountSPFCreditLimit$(): Observable<number | null> {
        return this.getCustomerAccount$().pipe(map((account) => account?.['variableTermThresholdAmount'] || null));
    }

    isGuest(): boolean {
        const isGuest = this.getCustomer()?.isGuest;
        return !!isGuest;
    }

    getUpdateCustomerAddress(salesTaxForm?: SalesTaxInfo | null): Observable<SalesTaxConfirmationResponse> {
        if (!this.isGuest() && salesTaxForm) {
            return this.customerApiService
                .updateCustomer(salesTaxForm)
                .pipe(map(() => ({ saveInfo: true, salesTaxForm })));
        }

        return of({ saveInfo: false, salesTaxForm: undefined });
    }

    isTaxEligibleByUser(errorResponse?: unknown): boolean {
        if (!errorResponse && this.isGuest()) {
            // return true && this.statService.isFeatureEnabled(`taxjar`);
            return false;
        }

        if (!(errorResponse instanceof PaymentRequestException)) {
            return false;
        }

        const taxEligibleResponse = errorResponse as PaymentRequestException<
            TaxEligibleResponse | TaxEligibleResponse[]
        >;

        return taxEligibleResponse.code === PaymentRequestExceptionType.TAX_ELIGIBLE;
    }

    public validateTaxEligibleResponse(
        taxEligible: boolean,
        salesTaxInfo?: SalesTaxInfo | null
    ): Observable<SalesTaxConfirmationResponse & { billingInfo?: TaxAddressPayload }> {
        if (taxEligible && !this.isGuest()) {
            return this.getUpdateCustomerAddress(salesTaxInfo).pipe(
                map((response) => ({
                    ...response,
                    billingInfo: undefined,
                }))
            );
        }

        return of({
            saveInfo: true,
            payload: undefined,
            billingInfo: this.getGuestTaxAddress(salesTaxInfo),
        });
    }

    public getGuestTaxAddress(salesTaxInfo: SalesTaxInfo | null | undefined): TaxAddressPayload | undefined {
        const shouldIncludeBillingInfo = this.isGuest() && salesTaxInfo;

        return shouldIncludeBillingInfo
            ? {
                  state: salesTaxInfo?.state ?? '',
                  zipCode: salesTaxInfo?.zipCode ?? '',
              }
            : undefined;
    }
}
